import { useState, useEffect, useRef } from 'react';

export default function ProcessSteps() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          }
        });
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  const steps = [
    {
      number: '01',
      title: 'Phân tích & làm rõ bài toán',
      points: [
        'Hiểu đúng quy trình vận hành thực tế',
        'Xác định vấn đề, điểm nghẽn, rủi ro',
        'Chuẩn hóa yêu cầu nghiệp vụ, tránh phát sinh'
      ],
      result: 'Làm đúng ngay từ đầu, giảm chi phí về sau'
    },
    {
      number: '02',
      title: 'Thiết kế giải pháp & kiến trúc hệ thống',
      points: [
        'Thiết kế hệ thống phù hợp quy mô hiện tại và tương lai',
        'Định hướng kiến trúc rõ ràng (mở rộng, tích hợp, bảo mật)',
        'Không phụ thuộc công nghệ hay nền tảng đóng'
      ],
      result: 'Hệ thống có thể phát triển cùng doanh nghiệp'
    },
    {
      number: '03',
      title: 'Phát triển phần mềm',
      points: [
        'Phát triển theo tiêu chuẩn kỹ thuật rõ ràng',
        'Code dễ bảo trì, dễ mở rộng',
        'Đảm bảo được phần mềm hoạt động ổn định, chính xác và hiệu năng cao'
      ],
      result: 'Phần mềm không chỉ chạy được mà phải đảm bảo đầy đủ các yếu tố từ hiệu năng đến bảo mật'
    },
    {
      number: '04',
      title: 'Kiểm thử, triển khai & bàn giao',
      points: [
        'Kiểm thử theo đúng nghiệp vụ thực tế',
        'Bàn giao đầy đủ tài liệu, mã nguồn',
        'Hỗ trợ triển khai và vận hành'
      ],
      result: 'Doanh nghiệp làm chủ hoàn toàn hệ thống'
    }
  ];

  return (
    <section 
      ref={sectionRef}
      className="py-24" 
      style={{ backgroundColor: '#f6f7f8' }}
    >
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className={`text-center mb-20 transition-all duration-1000 delay-200 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          <h2 className="text-4xl lg:text-5xl font-bold text-[#2a4b7c] mb-6">
            CÁCH STACKWAY TRIỂN KHAI
          </h2>
          <p className="text-base md:text-lg lg:text-xl text-gray-600 max-w-5xl mx-auto">
            Stackway tiếp cận từ <strong className="text-[#06b6d4]">nghiệp vụ – kiến trúc – công nghệ</strong>, không bắt đầu bằng code
          </p>
        </div>

        <div className="space-y-16">
          {steps.map((step, index) => (
            <div
              key={index}
              className={`flex flex-col lg:flex-row gap-8 items-start transition-all duration-1000 ${
                index % 2 === 1 ? 'lg:flex-row-reverse' : ''
              } ${
                isVisible ? 'opacity-100 translate-x-0' : index % 2 === 1 ? 'opacity-0 translate-x-12' : 'opacity-0 -translate-x-12'
              }`}
              style={{ transitionDelay: `${400 + index * 200}ms` }}
            >
              <div className="lg:w-1/3 flex-shrink-0">
                <div className="relative">
                  <div className="text-8xl font-bold text-[#06b6d4] opacity-20">
                    {step.number}
                  </div>
                  <h3 className="text-2xl lg:text-3xl font-bold text-[#2a4b7c] mt-4 leading-tight">
                    {step.title}
                  </h3>
                </div>
              </div>

              <div className="lg:w-2/3 bg-gradient-to-br from-gray-50 to-white p-8 rounded-2xl border border-gray-200 hover:border-[#06b6d4] transition-all duration-300 hover:shadow-xl">
                <ul className="space-y-4 mb-6">
                  {step.points.map((point, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <div className="w-6 h-6 flex items-center justify-center bg-[#00c897] rounded-full flex-shrink-0 mt-0.5">
                        <i className="ri-check-line text-white text-sm"></i>
                      </div>
                      <span className="text-gray-700 leading-relaxed">{point}</span>
                    </li>
                  ))}
                </ul>

                <div className="pt-6 border-t border-gray-200">
                  <div className="flex items-start gap-3">
                    <i className="ri-arrow-right-circle-fill text-2xl text-[#00c897] flex-shrink-0"></i>
                    <p className="text-[#2a4b7c] font-semibold leading-relaxed">
                      {step.result}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}